/**
 * Public analytics facade — the ONLY module application code imports to emit
 * analytics (mirrors the single-touchpoint rule for `useWallet()`). Holds
 * compile-time-safe wrappers over the dispatcher and self-registers the
 * production sinks on first import.
 *
 *   import { analytics } from "#/lib/analytics";
 *   analytics.track("login_started", { entry_point: "header" });
 *
 * Adding an event → extend `AnalyticsEventMap` in `events.ts` (type-checked at
 * every call site). Adding a provider → register a new sink below. See
 * `ANALYTICS.md` for both recipes.
 */
import {
	dispatchIdentify,
	dispatchPage,
	dispatchReset,
	dispatchTrack,
	registerSink,
} from "./core";
import type { AnalyticsEventMap } from "./events";
import { amplitudeSink } from "./sinks/amplitude";
import { googleAnalyticsSink } from "./sinks/google-analytics";
import type { UserTraits } from "./types";

// --- Sink registration (side effect on first import) -----------------------
// Add new production sinks here (PostHog, BackendSink, …). Order is irrelevant.
registerSink(googleAnalyticsSink);
registerSink(amplitudeSink);

// --- Facade ----------------------------------------------------------------
export const analytics = {
	/** Emit a typed event. `name` + `props` are checked against the catalog. */
	track<K extends keyof AnalyticsEventMap>(
		name: K,
		props: AnalyticsEventMap[K],
	): void {
		dispatchTrack(name, props as Record<string, unknown>);
	},
	/** Associate subsequent events with a user + set user-scoped properties. */
	identify(traits: UserTraits): void {
		dispatchIdentify(traits);
	},
	/** Record an explicit pageview. */
	page(path: string): void {
		dispatchPage(path);
	},
	/** Clear user association (on logout). */
	reset(): void {
		dispatchReset();
	},
};

export { captureFirstTouch, getAttribution } from "./attribution";
export {
	buildSignupPayload,
	hasFiredSignup,
	isNewAccount,
	markSignupFired,
} from "./auth-funnel";
// --- Re-exports (one import surface for sync components) --------------------
export type {
	AnalyticsEventMap,
	AnalyticsEventName,
	AuthMethod,
	LandingCtaLocation,
	LoginEntryPoint,
} from "./events";
export type { AnalyticsSink, AttributionData, UserTraits } from "./types";
